import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useAuth from "../../../hooks/useAuth";
import { getTruckComplianceMatrix } from "../../../api/truckComplianceService";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";
import { truckComplianceMatrixColumns } from "../../../data/FormColumns/Fleet/truckComplianceMatrixColumns";

const normalize = (value?: string) => (value ?? "").toLowerCase();

const TruckComplianceMatrixTable = () => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [unitNumberFilter, setUnitNumberFilter] = useState("");
  const [billToCompanyFilter, setBillToCompanyFilter] = useState("");
  const [authorityCompanyFilter, setAuthorityCompanyFilter] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["truckComplianceMatrix", token],
    enabled: !!token,
    queryFn: () => getTruckComplianceMatrix(token ?? undefined),
  });

  const rows = useMemo(() => {
    if (!data) return [];

    return data
      .map((item) => ({
        id: item.truckId,
        ...item,
      }))
      .filter((item) => {
        const byUnit = normalize(item.unitNumber).includes(
          normalize(unitNumberFilter),
        );
        const byCompany = normalize(item.billToCompanyName).includes(
          normalize(billToCompanyFilter),
        );
        const byAuthorityCompany = normalize(
          item.authorityCompanyName,
        ).includes(normalize(authorityCompanyFilter));

        const globalSearchFields = [
          item.unitNumber,
          item.transactionType,
          item.billToCompanyName,
          item.authorityCompanyName,
          item.hasLeaseAgreement ? "verified" : "missing",
          item.hasCertificateOfInsurance ? "verified" : "missing",
          item.hasRegistration ? "verified" : "missing",
          item.hasAnnualInspection ? "verified" : "missing",
        ];

        const bySearch = globalSearchFields.some((field) =>
          normalize(field).includes(normalize(searchTerm)),
        );

        return byUnit && byCompany && byAuthorityCompany && bySearch;
      });
  }, [
    authorityCompanyFilter,
    billToCompanyFilter,
    data,
    searchTerm,
    unitNumberFilter,
  ]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error">Error: {(error as Error).message}</Typography>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
        <TextField
          label="Search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search matrix"
          fullWidth
        />
        <TextField
          label="Filter by Unit Number"
          value={unitNumberFilter}
          onChange={(event) => setUnitNumberFilter(event.target.value)}
          placeholder="Unit number"
          fullWidth
        />
        <TextField
          label="Filter by Authority Company"
          value={authorityCompanyFilter}
          onChange={(event) => setAuthorityCompanyFilter(event.target.value)}
          placeholder="Authority company name"
          fullWidth
        />
        <TextField
          label="Filter by Bill To Company"
          value={billToCompanyFilter}
          onChange={(event) => setBillToCompanyFilter(event.target.value)}
          placeholder="Company name"
          fullWidth
        />
      </Stack>

      <PersistedDataGrid
        tableName="truckComplianceMatrix"
        exportFileName="truck_compliance_matrix"
        rows={rows}
        columns={truckComplianceMatrixColumns}
        autoHeight
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
              page: 0,
            },
          },
        }}
      />
    </Box>
  );
};

export default TruckComplianceMatrixTable;
